import { Component, EventEmitter, OnInit } from '@angular/core';
import { ECharts, EChartsOption } from 'echarts';
import { IDateRangePickerDashboardItem } from 'src/app/components/date-picker/date-picker.component';
import { IMultipleSelectDashboardItem } from 'src/app/components/multiple-select/multiple-select.component';
import { ChartColorService } from 'src/app/services/chart-color.service';
import { CommonLegendService } from 'src/app/services/common-legend.service';
import { IDashboardItemDataLoaderLink } from 'src/app/services/dashboard-item/IDashboardItemDataLoaderLink';
import { DashboardService } from 'src/app/services/dashboard/dashboard.service';
import { getGcpProjectIdForEnv } from 'src/app/services/data-environment/models/data-environment.model';
import { IDataLoaderResult } from 'src/app/services/data-loader/data-loader-result';
import { DataLoaderFactories } from 'src/app/services/data-loader/data-loader.service';
import {
    IQueryContext,
    QueryContextSettingID,
} from 'src/app/services/query-context/models/IQueryContext';
import { QueryContextService } from 'src/app/services/query-context/query-context.service';
import {
    DatasourceColumnId,
    datasourceColumnOrderByQuery,
} from 'src/app/utils/datasource-sql-map/datasource';
import { EChartsAxisHelper } from 'src/app/utils/echarts/echarts-axis-helper';
import { EChartsHelper } from 'src/app/utils/echarts/echarts-helper';
import { EChartsTooltipHelper } from 'src/app/utils/echarts/echarts-tooltip-helper';
import { GcpProjectId } from 'src/app/utils/gcp-project-id.model';
import { DashboardItemFactories } from '../common/items/common-dashboard-items';
import { FilterCategoryDashboardItemFactoryOptions } from '../common/items/filter/category-filter-dashboard-item/filter-category';
import { createDefaultSegmentDatasourceColumn } from '../common/items/filter/segment-filter/segments';
import { ModifyTimeDashboardItemFactoryOptions } from '../common/items/modify-time';
import { IMultiSelectSettingDashboardItem } from '../common/items/setting/multi-select/multi-select.component';
import { ISingleSelectSettingDashboardItem } from '../common/items/setting/single-select/single-select.component';
import { BiVizDashboardRetention } from '../common/retention/sql-maps/biviz-dashboard-retention-sql-map';
import { StandardDashboardWorker } from '../common/standard-dashboard';

@Component({
    selector: 'app-lastarchon-retention',
    templateUrl: './lastarchon-retention.component.html',
    styleUrls: ['./lastarchon-retention.component.scss'],
    providers: [DashboardService, QueryContextService, CommonLegendService],
})
export class LastArchonRetentionComponent
    extends StandardDashboardWorker
    implements OnInit
{
    override DASHBOARD_NAME = 'Retention';
    override DASHBOARD_ID = 'lastarchon-retention';
    keys = Object.keys;
    entries = Object.entries;
    values = Object.values;

    private gcpProjectId: GcpProjectId = 'am-lastarchon-dev';

    /////////////////////////////////////////////////////////////////
    ////
    ////    DATALOADERS
    ////    Some DataLoader can be used in multiple charts.
    ////    That's why there are created here and later referenced
    ////
    /////////////////////////////////////////////////////////////////

    override chartDataLoaders = {
        'lastarchon.retention.modify_time_retention_datasource':
            DataLoaderFactories['common.modify_time']({
                createdInstanceId:
                    'lastarchon.retention.modify_time_retention_datasource',

                gcpProjectId: () => {
                    return getGcpProjectIdForEnv(this.dataEnv.current);
                },
                queryCacheService: this.queryCache,
                datasetId: 'bi_viz',
                tableId: 'dashboard_retention',
            }).create(),
        'lastarchon.retention.retention_by_day_no': DataLoaderFactories[
            'common.retention.retention_by_day_no'
        ]({
            createdInstanceId: 'lastarchon.retention.retention_by_day_no',

            gcpProjectId: () => {
                return getGcpProjectIdForEnv(this.dataEnv.current);
            },
            queryCacheService: this.queryCache,
        }).create(),
        'lastarchon.retention.retention_by_first_open_date':
            DataLoaderFactories[
                'common.retention.retention_by_first_open_date'
            ]({
                createdInstanceId:
                    'lastarchon.retention.retention_by_first_open_date',

                gcpProjectId: () => {
                    return getGcpProjectIdForEnv(this.dataEnv.current);
                },
                queryCacheService: this.queryCache,
                retentionType: 'retention',
            }).create(),
        'lastarchon.retention.rolling_retention_by_first_open_date':
            DataLoaderFactories[
                'common.retention.retention_by_first_open_date'
            ]({
                createdInstanceId:
                    'lastarchon.retention.rolling_retention_by_first_open_date',

                gcpProjectId: () => {
                    return getGcpProjectIdForEnv(this.dataEnv.current);
                },
                queryCacheService: this.queryCache,
                retentionType: 'rolling_retention',
            }).create(),
    };

    override filterDataLoaders = {
        // 'SEGMENTS':
        'lastarchon.retention.filter.segment': DataLoaderFactories[
            'common.filter.segment'
        ]({
            createdInstanceId: 'lastarchon.retention.filter_segments',
            gcpProjectId: () => {
                return getGcpProjectIdForEnv(this.dataEnv.current);
            },
            queryCacheService: this.queryCache,
            datasourceColumnId: 'SEGMENT',
        }).create(),
        // 'FIRST_GEO_COUNTRY_EU':
        'lastarchon.retention.filter.first_geo_country_eu': DataLoaderFactories[
            'common.filter.datasource_column'
        ]({
            createdInstanceId:
                'lastarchon.retention.filter.first_geo_country_eu',

            gcpProjectId: () => {
                return getGcpProjectIdForEnv(this.dataEnv.current);
            },
            queryCacheService: this.queryCache,
            datasourceColumnId: 'FIRST_GEO_COUNTRY_EU',
            datasourceSqlMap: BiVizDashboardRetention.sqlMap,
        }).create(),
        // 'FIRST_GEO_COUNTRY_TIER':
        'lastarchon.retention.filter.first_geo_country_tier':
            DataLoaderFactories['common.filter.datasource_column']({
                createdInstanceId:
                    'lastarchon.retention.filter.first_geo_country_tier',

                gcpProjectId: () => {
                    return getGcpProjectIdForEnv(this.dataEnv.current);
                },
                queryCacheService: this.queryCache,
                datasourceColumnId: 'FIRST_GEO_COUNTRY_TIER',
                datasourceSqlMap: BiVizDashboardRetention.sqlMap,
            }).create(),
        // 'FIRST_GEO_COUNTRY':
        'lastarchon.retention.filter.first_geo_country': DataLoaderFactories[
            'common.filter.datasource_column'
        ]({
            createdInstanceId: 'lastarchon.retention.filter.first_geo_country',

            gcpProjectId: () => {
                return getGcpProjectIdForEnv(this.dataEnv.current);
            },
            queryCacheService: this.queryCache,
            datasourceColumnId: 'FIRST_GEO_COUNTRY',
            datasourceSqlMap: BiVizDashboardRetention.sqlMap,
            filterContext: ['FIRST_GEO_COUNTRY_TIER'],
        }).create(),
        // 'FIRST_ATTRIBUTION_UNIFIED_TRAFFIC':
        'lastarchon.retention.filter.attribution_unified_traffic':
            DataLoaderFactories['common.filter.datasource_column']({
                createdInstanceId:
                    'lastarchon.retention.filter.attribution_unified_traffic',

                gcpProjectId: () => {
                    return getGcpProjectIdForEnv(this.dataEnv.current);
                },
                queryCacheService: this.queryCache,
                datasourceColumnId: 'FIRST_ATTRIBUTION_UNIFIED_TRAFFIC',
                datasourceSqlMap: BiVizDashboardRetention.sqlMap,
            }).create(),
        // 'FIRST_APP_VERSION':
        'lastarchon.retention.filter.first_app_version': DataLoaderFactories[
            'common.filter.datasource_column'
        ]({
            createdInstanceId: 'lastarchon.retention.filter.first_app_version',

            gcpProjectId: () => {
                return getGcpProjectIdForEnv(this.dataEnv.current);
            },
            queryCacheService: this.queryCache,
            datasourceColumnId: 'FIRST_APP_VERSION',
            datasourceSqlMap: BiVizDashboardRetention.sqlMap,
            filterContext: true,
            orderByQuery: datasourceColumnOrderByQuery('FIRST_APP_VERSION'),
        }).create(),
        'lastarchon.retention.filter.first_app_version_group_minor':
            DataLoaderFactories['common.filter.datasource_column']({
                createdInstanceId:
                    'lastarchon.retention.filter.first_app_version_group_minor',

                gcpProjectId: () => {
                    return getGcpProjectIdForEnv(this.dataEnv.current);
                },
                queryCacheService: this.queryCache,
                datasourceColumnId: 'FIRST_APP_VERSION_GROUP_MINOR',
                datasourceSqlMap: BiVizDashboardRetention.sqlMap,
                filterContext: true,
                orderByQuery: datasourceColumnOrderByQuery(
                    'FIRST_APP_VERSION_GROUP_MINOR',
                ),
            }).create(),
        // 'REVENUE_PAYER':
        'lastarchon.retention.filter.etl_payer': DataLoaderFactories[
            'common.filter.datasource_column'
        ]({
            createdInstanceId: 'lastarchon.retention.filter.etl_payer',

            gcpProjectId: () => {
                return getGcpProjectIdForEnv(this.dataEnv.current);
            },
            queryCacheService: this.queryCache,
            datasourceColumnId: 'REVENUE_PAYER',
            datasourceSqlMap: BiVizDashboardRetention.sqlMap,
        }).create(),
        // 'REVENUE_BADGE':
        'lastarchon.retention.filter.etl_revenue_badge': DataLoaderFactories[
            'common.filter.datasource_column'
        ]({
            createdInstanceId: 'lastarchon.retention.filter.etl_revenue_badge',

            gcpProjectId: () => {
                return getGcpProjectIdForEnv(this.dataEnv.current);
            },
            queryCacheService: this.queryCache,
            datasourceColumnId: 'REVENUE_BADGE',
            datasourceSqlMap: BiVizDashboardRetention.sqlMap,
            orderByQuery: datasourceColumnOrderByQuery('REVENUE_BADGE'),
        }).create(),
        // 'FIRST_ATTRIBUTION_UNIFIED_NETWORK':
        'lastarchon.retention.filter.attribution_network_name':
            DataLoaderFactories['common.filter.datasource_column']({
                createdInstanceId:
                    'lastarchon.retention.filter.attribution_network_name',

                gcpProjectId: () => {
                    return getGcpProjectIdForEnv(this.dataEnv.current);
                },
                queryCacheService: this.queryCache,
                datasourceColumnId: 'FIRST_ATTRIBUTION_UNIFIED_NETWORK',
                datasourceSqlMap: BiVizDashboardRetention.sqlMap,
            }).create(),
        // 'FIRST_ATTRIBUTION_UNIFIED_CAMPAIGN':
        'lastarchon.retention.filter.attribution_campaign_name':
            DataLoaderFactories['common.filter.datasource_column']({
                createdInstanceId:
                    'lastarchon.retention.filter.attribution_campaign_name',

                gcpProjectId: () => {
                    return getGcpProjectIdForEnv(this.dataEnv.current);
                },
                queryCacheService: this.queryCache,
                datasourceColumnId: 'FIRST_ATTRIBUTION_UNIFIED_CAMPAIGN',
                datasourceSqlMap: BiVizDashboardRetention.sqlMap,
                filterContext: true,
            }).create(),
    };

    // spread nie sklonuje obiektów DataLoader, tylko przepisze ich referencje
    allDataLoaders = { ...this.chartDataLoaders, ...this.filterDataLoaders };

    /////////////////////////////////////////////////////////////////
    ////
    ////    [DASHBOARD ITEM <-> DATALOADER] LINKS
    ////
    /////////////////////////////////////////////////////////////////

    settingsCards = {
        retentionMaxDayNo: {
            options: {
                id: QueryContextSettingID.RETENTION_MAX_DAY_NO,
                viewtype: 'singleSelect',
                displayName: 'Limit max day no',
                value: this.getInitialContext().settings[
                    QueryContextSettingID.RETENTION_MAX_DAY_NO
                ],
                selectOptions: ['60', '90', '180', '360', '720', 'unlimited'],
            },
        } as ISingleSelectSettingDashboardItem,
        retentionSelectedDayNo: {
            options: {
                id: QueryContextSettingID.RETENTION_SELECTED_DAY_NO,
                viewtype: 'multiSelect',
                displayName: 'Selected day no',
                value: this.getInitialContext().settings[
                    QueryContextSettingID.RETENTION_SELECTED_DAY_NO
                ],
                selectOptions: [
                    '1',
                    '3',
                    '7',
                    '14',
                    '30',
                    '90',
                    '180',
                    '360',
                ].map((v) => ({ value: v, label: v })),
            },
        } as IMultiSelectSettingDashboardItem,
        dateAggregation: {
            options: {
                id: QueryContextSettingID.DATE_AGGREGATION,
                viewtype: 'singleSelect',
                displayName: 'Date aggregation',
                value: 'DAY',
                selectOptions: ['DAY', 'WEEK', 'MONTH', 'QUARTER', 'YEAR'],
            },
        } as ISingleSelectSettingDashboardItem,
    };

    // CHARTS
    modify_time_retention_datasource: IDashboardItemDataLoaderLink = {
        id: 'lastarchon.retention.modify_time_retention_datasource',
        dashboardItemFactory: DashboardItemFactories['common.modify_time'](
            new ModifyTimeDashboardItemFactoryOptions(
                'lastarchon.retention.modify_time_retention_datasource',
                'value',
            ),
        ),
        dataLoaderEmitter:
            this.chartDataLoaders[
                'lastarchon.retention.modify_time_retention_datasource'
            ].emitter,
    };
    retention_per_day_no: IDashboardItemDataLoaderLink = {
        id: 'lnk.lastarchon.retention.chart.retention_by_day_no',
        dashboardItemFactory: DashboardItemFactories['common.chart.xy_chart']({
            dashboardName: this.DASHBOARD_NAME,
            createdInstanceId: 'lastarchon.retention.chart.retention_by_day_no',
            dashboardItem: { title: 'Retention by day_no' },
            xColumnLabel: 'day_no',
            yColumnLabel: 'retention',
            metricName: 'Retention',
            echartsSeriesType: 'line',
            xAxisType: 'value',
            colorGetterBuilder: (context: IQueryContext) => {
                // get all split columns ids
                const splitColumnsIds = context.datasourceColumns
                    .filter((dc) => dc.state.isSplitApplied)
                    .map((dc) => dc.id);
                // create layer from spliColumns
                const layer = ChartColorService.getLayerString(splitColumnsIds);
                return (s: string) => ({
                    color: this.colorService.getColor(layer, s),
                });
            },
            echartsOptionsOverride: (
                context: IQueryContext,
                echartsOption: EChartsOption,
                echartsInstance: ECharts,
            ) => {
                echartsOption.yAxis = {
                    axisLabel: {
                        formatter:
                            EChartsAxisHelper.getAxisPercentLabelFormatter(0),
                    },
                };
                echartsOption.tooltip =
                    EChartsTooltipHelper.createDefaultTooltip(echartsInstance, {
                        xLabelFormatter: (label: any) => `Day no: ${label}`,
                        valueFormatter:
                            EChartsTooltipHelper.tooltipPercentValueFormatter,
                    });
            },
        }),
        dataLoaderEmitter:
            this.chartDataLoaders['lastarchon.retention.retention_by_day_no']
                .emitter,
    };
    rolling_retention_per_day_no: IDashboardItemDataLoaderLink = {
        id: 'lnk.lastarchon.retention.chart.rolling_retention_by_day_no',
        dashboardItemFactory: DashboardItemFactories['common.chart.xy_chart']({
            dashboardName: this.DASHBOARD_NAME,
            createdInstanceId:
                'lastarchon.retention.chart.rolling_retention_by_day_no',

            dashboardItem: { title: 'Rolling retention by day_no' },
            xColumnLabel: 'day_no',
            yColumnLabel: 'rolling_retention',
            metricName: 'Rolling retention',
            echartsSeriesType: 'line',
            xAxisType: 'value',
            colorGetterBuilder: (context: IQueryContext) => {
                // get all split columns ids
                const splitColumnsIds = context.datasourceColumns
                    .filter((dc) => dc.state.isSplitApplied)
                    .map((dc) => dc.id);
                // create layer from spliColumns
                const layer = ChartColorService.getLayerString(splitColumnsIds);
                return (s: string) => ({
                    color: this.colorService.getColor(layer, s),
                });
            },
            echartsOptionsOverride: (
                context: IQueryContext,
                echartsOption: EChartsOption,
                echartsInstance: ECharts,
            ) => {
                echartsOption.yAxis = {
                    axisLabel: {
                        formatter:
                            EChartsAxisHelper.getAxisPercentLabelFormatter(0),
                    },
                };
                echartsOption.tooltip =
                    EChartsTooltipHelper.createDefaultTooltip(echartsInstance, {
                        xLabelFormatter: (label: any) => `Day no: ${label}`,
                        valueFormatter:
                            EChartsTooltipHelper.tooltipPercentValueFormatter,
                    });
            },
        }),
        dataLoaderEmitter:
            this.chartDataLoaders[
                'lastarchon.retention.retention_by_day_no'
            ].emitter,
    };
    retention_by_first_open_date: IDashboardItemDataLoaderLink = {
        id: 'lnk.lastarchon.retention.chart.retention_by_first_open_date',
        dashboardItemFactory: DashboardItemFactories['common.chart.xy_chart']({
            dashboardName: this.DASHBOARD_NAME,
            createdInstanceId:
                'lastarchon.retention.chart.retention_per_day_no',
            dashboardItem: { title: 'Retention by first open date' },
            xColumnLabel: 'date',
            metricsGetter: EChartsHelper.createRetentionMetricsGetter({
                metricId: 'retention',
                metricName: 'Retention',
            }),
            colorGetterBuilder: (context: IQueryContext) => {
                // get all split columns ids
                const splitColumnsIds = context.datasourceColumns
                    .filter((dc) => dc.state.isSplitApplied)
                    .map((dc) => dc.id);
                // create layer from spliColumns
                const layer = ChartColorService.getLayerString(splitColumnsIds);
                return (s: string) => ({
                    color: this.colorService.getColor(layer, s),
                });
            },
            echartsOptionsOverride: (
                context: IQueryContext,
                echartsOption: EChartsOption,
                echartsInstance: ECharts,
            ) => {
                echartsOption.yAxis = {
                    axisLabel: {
                        formatter:
                            EChartsAxisHelper.getAxisPercentLabelFormatter(0),
                    },
                };
                echartsOption.tooltip =
                    EChartsTooltipHelper.createDefaultTooltip(echartsInstance, {
                        xLabelFormatter: (label: any) =>
                            EChartsAxisHelper.axisLabelDateFormatter(
                                label,
                                context,
                            ),
                        valueFormatter:
                            EChartsTooltipHelper.tooltipPercentValueFormatter,
                    });
            },
        }),
        dataLoaderEmitter:
            this.chartDataLoaders[
                'lastarchon.retention.retention_by_first_open_date'
            ].emitter,
    };
    rolling_retention_by_first_open_date: IDashboardItemDataLoaderLink = {
        id: 'lnk.lastarchon.retention.chart.rolling_retention_by_first_open_date',
        dashboardItemFactory: DashboardItemFactories['common.chart.xy_chart']({
            dashboardName: this.DASHBOARD_NAME,
            createdInstanceId:
                'lastarchon.retention.chart.rolling_retention_by_first_open_date',
            dashboardItem: { title: 'Rolling retention by first open date' },
            xColumnLabel: 'date',
            yColumnLabel: 'retention',
            metricName: 'Rolling retention',
            metricsGetter: EChartsHelper.createRetentionMetricsGetter({
                metricId: 'retention',
                metricName: 'Rolling retention',
            }),
            colorGetterBuilder: (context: IQueryContext) => {
                // get all split columns ids
                const splitColumnsIds = context.datasourceColumns
                    .filter((dc) => dc.state.isSplitApplied)
                    .map((dc) => dc.id);
                // create layer from spliColumns
                const layer = ChartColorService.getLayerString(splitColumnsIds);
                return (s: string) => ({
                    color: this.colorService.getColor(layer, s),
                });
            },
            echartsOptionsOverride: (
                context: IQueryContext,
                echartsOption: EChartsOption,
                echartsInstance: ECharts,
            ) => {
                echartsOption.yAxis = {
                    axisLabel: {
                        formatter:
                            EChartsAxisHelper.getAxisPercentLabelFormatter(0),
                    },
                };
                echartsOption.tooltip =
                    EChartsTooltipHelper.createDefaultTooltip(echartsInstance, {
                        xLabelFormatter: (label: any) =>
                            EChartsAxisHelper.axisLabelDateFormatter(
                                label,
                                context,
                            ),
                        valueFormatter:
                            EChartsTooltipHelper.tooltipPercentValueFormatter,
                    });
            },
        }),
        dataLoaderEmitter:
            this.chartDataLoaders[
                'lastarchon.retention.rolling_retention_by_first_open_date'
            ].emitter,
    };
    avg_user_lifetime: IDashboardItemDataLoaderLink = {
        id: 'lnk.lastarchon.retention.chart.avg_user_lifetime',
        dashboardItemFactory: DashboardItemFactories['common.chart.xy_chart']({
            dashboardName: this.DASHBOARD_NAME,
            createdInstanceId: 'lastarchon.retention.chart.avg_user_lifetime',
            dashboardItem: { title: "Average users's lifetime" },
            xColumnLabel: 'day_no',
            yColumnLabel: 'avg_user_lifetime',
            metricName: "Avg users's lifetime",
            echartsSeriesType: 'line',
            xAxisType: 'value',
            colorGetterBuilder: (context: IQueryContext) => {
                // get all split columns ids
                const splitColumnsIds = context.datasourceColumns
                    .filter((dc) => dc.state.isSplitApplied)
                    .map((dc) => dc.id);
                // create layer from spliColumns
                const layer = ChartColorService.getLayerString(splitColumnsIds);
                return (s: string) => ({
                    color: this.colorService.getColor(layer, s),
                });
            },
            echartsOptionsOverride: (
                context: IQueryContext,
                echartsOption: EChartsOption,
                echartsInstance: ECharts,
            ) => {
                echartsOption.tooltip =
                    EChartsTooltipHelper.createDefaultTooltip(echartsInstance, {
                        xLabelFormatter: (label: any) => `Day no: ${label}`,
                        valueFormatter: (v: any) =>
                            EChartsTooltipHelper.tooltipNumberValueFormatter(
                                v,
                                ' days',
                            ),
                    });
            },
        }),
        dataLoaderEmitter:
            this.chartDataLoaders['lastarchon.retention.retention_by_day_no']
                .emitter,
    };

    filterDashboardItemLinks: Partial<
        Record<DatasourceColumnId, IDashboardItemDataLoaderLink>
    > = {
        SEGMENT: {
            id: 'lastarchon.retention.filter.segments',
            dashboardItemFactory: DashboardItemFactories['filter.segments']({
                createdInstanceId: 'lastarchon.retention.filter.segments',
            }),
            // empty emitter DataRangePicker doesn't need data
            dataLoaderEmitter:
                this.filterDataLoaders['lastarchon.retention.filter.segment']
                    .emitter,
        },
        FIRST_SESSION_DATE: {
            id: 'lastarchon.retention.filter.FIRST_SESSION_DATE',
            dashboardItemFactory: DashboardItemFactories['filter.daterange']({
                createdInstanceId:
                    'lastarchon.retention.filter.FIRST_SESSION_DATE',
                datasourceColumnId: 'FIRST_SESSION_DATE',
                splitable: true,
            }),
            // empty emitter DataRangePicker doesn't need data
            dataLoaderEmitter: new EventEmitter<IDataLoaderResult>(),
        },
        FIRST_GEO_COUNTRY_EU: {
            id: 'lastarchon.retention.filter.first_geo_country_eu',
            dashboardItemFactory: DashboardItemFactories['filter.category'](
                new FilterCategoryDashboardItemFactoryOptions(
                    'lastarchon.retention.filter.first_geo_country_eu',
                    'FIRST_GEO_COUNTRY_EU',
                ),
            ),
            dataLoaderEmitter:
                this.filterDataLoaders[
                    'lastarchon.retention.filter.first_geo_country_eu'
                ].emitter,
        },
        FIRST_GEO_COUNTRY_TIER: {
            id: 'lastarchon.retention.filter.first_geo_country_tier',
            dashboardItemFactory: DashboardItemFactories['filter.category'](
                new FilterCategoryDashboardItemFactoryOptions(
                    'lastarchon.retention.filter.first_geo_country_tier',
                    'FIRST_GEO_COUNTRY_TIER',
                ),
            ),
            dataLoaderEmitter:
                this.filterDataLoaders[
                    'lastarchon.retention.filter.first_geo_country_tier'
                ].emitter,
        },
        FIRST_GEO_COUNTRY: {
            id: 'lastarchon.retention.filter.first_geo_country',
            dashboardItemFactory: DashboardItemFactories['filter.category'](
                new FilterCategoryDashboardItemFactoryOptions(
                    'lastarchon.retention.filter.first_geo_country',
                    'FIRST_GEO_COUNTRY',
                ),
            ),
            dataLoaderEmitter:
                this.filterDataLoaders[
                    'lastarchon.retention.filter.first_geo_country'
                ].emitter,
        },
        FIRST_ATTRIBUTION_UNIFIED_TRAFFIC: {
            id: 'lastarchon.retention.filter.attribution_unified_traffic',
            dashboardItemFactory: DashboardItemFactories['filter.category'](
                new FilterCategoryDashboardItemFactoryOptions(
                    'lastarchon.retention.filter.attribution_network_name',
                    'FIRST_ATTRIBUTION_UNIFIED_TRAFFIC',
                ),
            ),
            dataLoaderEmitter:
                this.filterDataLoaders[
                    'lastarchon.retention.filter.attribution_unified_traffic'
                ].emitter,
        },
        FIRST_APP_VERSION_GROUP_MINOR: {
            id: 'lnk.lastarchon.retention.filter.first_app_version_group_minor',
            dashboardItemFactory: DashboardItemFactories['filter.category'](
                new FilterCategoryDashboardItemFactoryOptions(
                    'di.lastarchon.retention.filter.first_app_version_group_minor',
                    'FIRST_APP_VERSION_GROUP_MINOR',
                ),
            ),
            dataLoaderEmitter:
                this.filterDataLoaders[
                    'lastarchon.retention.filter.first_app_version_group_minor'
                ].emitter,
        },
        // 'FIRST_APP_VERSION':
        FIRST_APP_VERSION: {
            id: 'lnk.lastarchon.retention.filter.first_app_version',
            dashboardItemFactory: DashboardItemFactories['filter.category'](
                new FilterCategoryDashboardItemFactoryOptions(
                    'di.lastarchon.retention.filter.first_app_version',
                    'FIRST_APP_VERSION',
                ),
            ),
            dataLoaderEmitter:
                this.filterDataLoaders[
                    'lastarchon.retention.filter.first_app_version'
                ].emitter,
        },
        // 'REVENUE_PAYER':
        REVENUE_PAYER: {
            id: 'lastarchon.retention.filter.etl_payer',
            dashboardItemFactory: DashboardItemFactories['filter.category'](
                new FilterCategoryDashboardItemFactoryOptions(
                    'lastarchon.retention.filter.etl_payer',
                    'REVENUE_PAYER',
                ),
            ),
            dataLoaderEmitter:
                this.filterDataLoaders['lastarchon.retention.filter.etl_payer']
                    .emitter,
        },
        REVENUE_BADGE: {
            id: 'lastarchon.retention.filter.etl_revenue_badge',
            dashboardItemFactory: DashboardItemFactories['filter.category'](
                new FilterCategoryDashboardItemFactoryOptions(
                    'lastarchon.retention.filter.etl_revenue_badge',
                    'REVENUE_BADGE',
                ),
            ),
            dataLoaderEmitter:
                this.filterDataLoaders[
                    'lastarchon.retention.filter.etl_revenue_badge'
                ].emitter,
        },
        FIRST_ATTRIBUTION_UNIFIED_NETWORK: {
            id: 'lastarchon.retention.filter.attribution_network_name',
            dashboardItemFactory: DashboardItemFactories['filter.category'](
                new FilterCategoryDashboardItemFactoryOptions(
                    'lastarchon.retention.filter.attribution_network_name',
                    'FIRST_ATTRIBUTION_UNIFIED_NETWORK',
                ),
            ),
            dataLoaderEmitter:
                this.filterDataLoaders[
                    'lastarchon.retention.filter.attribution_network_name'
                ].emitter,
        },
        FIRST_ATTRIBUTION_UNIFIED_CAMPAIGN: {
            id: 'lastarchon.retention.filter.attribution_campaign_name',
            dashboardItemFactory: DashboardItemFactories['filter.category'](
                new FilterCategoryDashboardItemFactoryOptions(
                    'lastarchon.retention.filter.attribution_campaign_name',
                    'FIRST_ATTRIBUTION_UNIFIED_CAMPAIGN',
                ),
            ),
            dataLoaderEmitter:
                this.filterDataLoaders[
                    'lastarchon.retention.filter.attribution_campaign_name'
                ].emitter,
        },
    };

    override ngOnInit() {
        super.ngOnInit();
        console.log({
            msg: 'LastArchonRetentionComponent.ngOnInit',
            cancellationToken: this.cancellationToken,
        });
    }

    ngAfterViewInit() {
        console.log({
            msg: 'LastArchonRetentionComponent.ngAfterViewInit',
            cancellationToken: this.cancellationToken,
        });

        this.standardDashboardInitialize();
    }

    override getInitialContext(): IQueryContext {
        const initialContext = QueryContextService.INITIAL_CONTEXT;

        const firebaseCountryTierIC: IMultipleSelectDashboardItem['datasourceColumn'] =
            {
                id: 'FIRST_GEO_COUNTRY_TIER',
                state: {
                    value: {
                        options: ['Tier 1', 'Tier 2', 'Tier 3'],
                        filterType: 'include',
                    },
                    // filterType: 'include',
                    isFilterApplied: true,
                    isSplitApplied: false,
                },
            };
        const activityDateIC: IDateRangePickerDashboardItem['datasourceColumn'] =
            {
                id: 'FIRST_SESSION_DATE',
                state: {
                    value: {
                        preset: 'last 365 days',
                        splitAggregate: 'day',
                    },
                    isFilterApplied: true,
                    isSplitApplied: false,
                },
            };
        const segmentIC = createDefaultSegmentDatasourceColumn();

        // set initialContext for AppStore to Google Play
        initialContext.datasourceColumns = [
            // firebaseAppStorcd ho
            activityDateIC,
            segmentIC,
        ];

        initialContext.settings = {
            [QueryContextSettingID.RETENTION_MAX_DAY_NO]: '180',
            [QueryContextSettingID.RETENTION_SELECTED_DAY_NO]: ['1', '7', '30'],
        };

        return initialContext;
    }
}
